import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { So5LineupImportsBatchStatus } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { CloseButton } from '@sorare/core/src/components/dialog/CloseButton';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { ImportLineupActions } from '../ImportLineupActions';
import { ImportLineupContent } from '../ImportLineupContent';
import { MinifiedImportToast } from '../MinifiedImportToast';
import type {
  ImportLineupQuery,
  ImportLineupQueryVariables,
} from './__generated__/index.graphql';

const Body = styled(Vertical)`
  padding: var(--double-unit);
  position: relative;
  isolation: isolate;
`;

const Header = styled(Horizontal)`
  position: sticky;
  top: 0;
  z-index: 1;
  justify-content: space-between;
  padding: var(--double-unit);
  background-color: var(--c-nd-50);
  border-bottom: 1px solid var(--c-nd-200);
`;

const Warning = styled(Horizontal)`
  padding: var(--intermediate-unit) var(--double-unit);
  background-color: rgba(var(--c-rgb-white), 0.1);
  border-radius: var(--intermediate-unit);
`;

const IMPORT_LINEUP_QUERY = gql`
  query ImportLineupQuery($id: String!) {
    so5 {
      so5LineupImportsBatch(id: $id) {
        id
        status
        so5LineupImports {
          id
        }
        ...ImportLineupContent_so5LineupImportsBatch
      }
    }
  }
  ${ImportLineupContent.fragments.so5LineupImportsBatch}
` as TypedDocumentNode<ImportLineupQuery, ImportLineupQueryVariables>;

type Props = {
  id: string | null;
  provider: string;
  onComplete: () => void;
};

export const ImportLineupModal = ({ id, provider, onComplete }: Props) => {
  const [open, setOpen] = useState(true);
  const { showNotification } = useSnackNotificationContext();
  const [polling, setPolling] = useState(false);

  const { data, loading } = useQuery(IMPORT_LINEUP_QUERY, {
    variables: {
      id: id!,
    },
    skip: !id,
    pollInterval: polling ? 1000 : 0,
    onCompleted: d => {
      const lineupsImportState = d.so5.so5LineupImportsBatch?.status;
      if (
        lineupsImportState &&
        [
          So5LineupImportsBatchStatus.DECLINED,
          So5LineupImportsBatchStatus.EXPIRED,
        ].includesValue(lineupsImportState) &&
        open
      ) {
        showNotification(
          lineupsImportState === So5LineupImportsBatchStatus.DECLINED
            ? 'importLinuepsDeclined'
            : 'importLinuepsExpired',
          {
            provider,
          }
        );
        setOpen(false);
        onComplete();
      }
    },
  });

  const batchId = data?.so5.so5LineupImportsBatch?.id;
  const importedLineupsCount =
    data?.so5.so5LineupImportsBatch?.so5LineupImports.length;
  const lineupsImportState = data?.so5.so5LineupImportsBatch?.status;

  if (lineupsImportState === So5LineupImportsBatchStatus.PROCESSED && polling) {
    setPolling(false);
  }

  if (
    !id ||
    !data ||
    loading ||
    !lineupsImportState ||
    [
      So5LineupImportsBatchStatus.DECLINED,
      So5LineupImportsBatchStatus.EXPIRED,
    ].includesValue(lineupsImportState)
  )
    return null;

  if (!open && lineupsImportState)
    return (
      <MinifiedImportToast
        onClick={() => setOpen(true)}
        lineupCount={importedLineupsCount!}
        lineupsImportState={lineupsImportState}
        onComplete={onComplete}
      />
    );

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      hideHeader
      footer={
        <ImportLineupActions
          id={batchId}
          lineupsImportState={lineupsImportState}
          lineupsCount={importedLineupsCount}
          onClose={() => setOpen(false)}
          onConfirm={() => setPolling(true)}
          onComplete={onComplete}
        />
      }
    >
      <Header>
        <HeadlineM>
          <FormattedMessage
            id="importLineup.title"
            defaultMessage="Import teams from {provider} ?"
            values={{ provider }}
          />
        </HeadlineM>
        <CloseButton onClose={() => setOpen(false)} />
      </Header>
      <Body gap={2}>
        <BodyM>
          <FormattedMessage
            id="importLineup.desc"
            defaultMessage="{lineupCount, plural, one {{lineupCount} team} other {{lineupCount} teams}}  will be imported from {provider}."
            values={{
              lineupCount: importedLineupsCount,
              provider,
            }}
          />
        </BodyM>
        <Warning>
          <FontAwesomeIcon icon={faInfoCircle} />
          <BodyM>
            <FormattedMessage
              id="importLineup.warning"
              defaultMessage="All your existing teams will be updated or deleted."
            />
          </BodyM>
        </Warning>
        <ImportLineupContent
          so5LineupImportsBatch={data.so5.so5LineupImportsBatch!}
        />
      </Body>
    </Dialog>
  );
};
